import * as React from 'react';
import Link from 'next/link';
import { cn } from '@/util/cn';

// Ranked resort tile used in Top-Rated grids on the home page and the
// region guide. One shape:
//   - square cover photo with hover zoom
//   - rank badge anchored top-right of the photo
//   - resort name (+ optional region) overlaid bottom-left, sitting on a
//     bottom-up gradient scrim so the labels stay legible over any photo
//
// Source pattern: extracted from `region/ski-resorts.tsx` Top10Section
// so home Top Resorts + Region Guide share one implementation.

export type ResortRankCardProps = {
  /** 1-based rank position. Rendered as "#1", "#2", etc. */
  rank: number;
  title: string;
  image: string;
  /** Optional region/country line overlaid under the title on the photo. */
  subtitle?: string;
  /** Wraps the card in next/link when set. */
  href?: string;
  className?: string;
};

export const ResortRankCard = ({
  rank, title, image, subtitle, href, className,
}: ResortRankCardProps) => {
  const body = (
    <div className="relative aspect-square w-full overflow-hidden rounded-md bg-dark-blue shadow-sm">
      <img
        src={image}
        alt={title}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
      />
      {/* Bottom-up navy scrim (brand --dark-blue) — keeps the overlaid
          name/region legible over any photo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-dark-blue/95 via-dark-blue/20 to-transparent"
      />
      <span
        className={cn(
          'absolute right-2.5 top-2.5 inline-flex h-8 min-w-8 items-center justify-center',
          'rounded-md bg-white/95 px-2 font-heading text-sm font-bold tabular-nums',
          'text-foreground shadow-sm',
        )}
      >
        {`#${rank}`}
      </span>
      <div className="absolute inset-x-0 bottom-0 p-3">
        <div className="text-sm font-bold leading-none text-white underline-offset-2 group-hover:underline md:text-base">
          {title}
        </div>
        {subtitle && (
          <div className="mt-0.5 text-xs text-blue-200">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={cn('group block no-underline hover:no-underline', className)}
      >
        {body}
      </Link>
    );
  }

  return (
    <div className={cn('group block', className)}>
      {body}
    </div>
  );
};
